/**
 * Comptes de l'offre Hybride = comptes du SERVEUR (le dirigeant doit pouvoir se connecter sur le site).
 *
 * En ligne : la connexion passe par le serveur (identifiants, rôle et état « actif » font foi) et le poste
 * mémorise une identité locale : id serveur, rôle, état, vérificateur salé du mot de passe (PBKDF2, jamais le
 * mot de passe) et date de vérification. Hors ligne : ouverture possible avec cette identité pendant 30 jours
 * après la dernière vérification en ligne ; le rôle est rafraîchi à chaque connexion en ligne.
 *
 * Un refus du serveur (mauvais mot de passe, compte verrouillé ou désactivé) ne retombe JAMAIS sur le cache.
 */
import { BusinessApiError } from '@infra/api/BusinessApiClient';
import { AuthError } from '@infra/auth/AuthService';
import { PasswordHasher } from '@infra/auth/PasswordHasher';
import type { Role, Session, User } from '@shared/types/User';
import { uuid } from '@shared/utils/uuid';

export const OFFLINE_IDENTITY_MAX_AGE_MS = 30 * 24 * 3_600_000;
const MAX_OFFLINE_ATTEMPTS = 5;
const OFFLINE_LOCKOUT_MS = 15 * 60_000;
const SESSION_DURATION_MS = 8 * 3_600_000;

export interface HybridRemoteUser {
  id: string;
  tenant_id: string;
  email: string;
  display_name: string;
  role: string;
}

export interface HybridLoginApi {
  loginUser(email: string, password: string): Promise<{ token: string; expires_at: string; user: HybridRemoteUser }>;
}

export interface HybridUserDirectory {
  findByEmail(email: string, tenantId?: string): Promise<User | undefined>;
  findById(id: string): Promise<User | null>;
  save(user: User): Promise<void>;
  remove(id: string): Promise<void>;
}

export interface HybridAuthDeps {
  api: HybridLoginApi;
  users: HybridUserDirectory;
  persistSession(session: Session): void;
  /**
   * Migration d'un compte LOCAL existant (poste Hybride d'avant la synchronisation) : crée le compte sur le
   * serveur quand c'est possible (premier administrateur d'un serveur vide). Vrai si le compte existe désormais
   * côté serveur.
   */
  provisionLegacy?: (user: User, password: string) => Promise<boolean>;
  /** Établit la session d'installation (jeton) avant l'appel serveur ; faux si injoignable. */
  ensureInstallation?: (tenantId: string | undefined) => Promise<boolean>;
  now?: () => number;
  hash?: (password: string, salt: string) => Promise<string>;
  verify?: (password: string, hash: string, salt: string) => Promise<boolean>;
  newSalt?: () => string;
}

/** `legacy` : compte local d'avant la synchronisation, pas encore créé sur le serveur. */
export type HybridLoginMode = 'online' | 'offline' | 'legacy';

export function normalizeServerRole(raw: string): Role {
  const r = String(raw ?? '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (r === 'super_admin' || r === 'superadmin') return 'super_admin';
  if (r === 'admin' || r === 'administrateur') return 'admin';
  if (r === 'rh' || r === 'gestionnaire_rh') return 'rh';
  if (r === 'comptable' || r === 'compta') return 'comptable';
  if (r === 'lecture' || r === 'lecture_seule') return 'lecture';
  if (r === 'salarie' || r === 'salarié') return 'salarie';
  return raw as Role;
}

function splitName(displayName: string): { prenom: string; nom: string } {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const prenom = parts[0] ?? '';
  return { prenom, nom: parts.slice(1).join(' ') || prenom };
}

/** Le serveur a répondu (même par un refus) : on ne retombe pas sur le cache local. */
function isServerAnswer(err: unknown): boolean {
  if (!(err instanceof BusinessApiError)) return false;
  const status = err.statusCode;
  if (status === undefined) return false;
  return status < 500 && status !== 429 && status !== 408;
}

export class HybridAuthService {
  private readonly failures = new Map<string, { count: number; lockedUntil?: number }>();
  private readonly now: () => number;
  private readonly hash: (password: string, salt: string) => Promise<string>;
  private readonly verify: (password: string, hash: string, salt: string) => Promise<boolean>;
  private readonly newSalt: () => string;

  constructor(private readonly deps: HybridAuthDeps) {
    this.now = deps.now ?? Date.now;
    this.hash = deps.hash ?? ((p, s) => PasswordHasher.hash(p, s));
    this.verify = deps.verify ?? ((p, h, s) => PasswordHasher.verify(p, h, s));
    this.newSalt = deps.newSalt ?? (() => PasswordHasher.generateSalt());
  }

  async login(input: { email: string; password: string; tenantId?: string }): Promise<{ session: Session; mode: HybridLoginMode }> {
    const email = input.email.toLowerCase().trim();
    let installationOk = true;
    try {
      installationOk = (await this.deps.ensureInstallation?.(input.tenantId)) ?? true;
    } catch {
      installationOk = false;
    }
    if (installationOk) {
      try {
        const result = await this.deps.api.loginUser(email, input.password);
        return { session: await this.rememberOnlineLogin(result, email, input.password, input.tenantId), mode: 'online' };
      } catch (err) {
        if (isServerAnswer(err)) {
          // Refus du serveur : mauvais mot de passe, compte verrouillé, licence… jamais de repli sur le cache.
          const refused = err as BusinessApiError;
          if (refused.code === 'account_locked') throw new AuthError('TOO_MANY_ATTEMPTS');
          if (refused.statusCode === 401 && !(refused.code ?? '').startsWith('installation')) {
            const legacy = await this.tryLegacyAccount(email, input.password, input.tenantId);
            if (legacy) return legacy;
            throw new AuthError('INVALID_CREDENTIALS');
          }
          throw err;
        }
        // Serveur injoignable (réseau coupé, délai, 5xx) : ouverture hors ligne avec l'identité mémorisée.
      }
    }
    return { session: await this.loginOffline(email, input.password, input.tenantId), mode: 'offline' };
  }

  /**
   * Le serveur ne connaît pas ce compte : si c'est un compte local d'avant la synchronisation (jamais vérifié en
   * ligne) et que le mot de passe local est correct, on le migre vers le serveur ou, à défaut, on ouvre la session
   * en mode « compte local » plutôt que de verrouiller le client dehors.
   */
  private async tryLegacyAccount(
    email: string,
    password: string,
    tenantId: string | undefined,
  ): Promise<{ session: Session; mode: HybridLoginMode } | null> {
    const legacy = await this.deps.users.findByEmail(email, tenantId);
    if (!legacy || legacy.identity_verified_at || !legacy.actif) return null;
    if (!(await this.verify(password, legacy.password_hash, legacy.password_salt))) return null;
    let provisioned = false;
    try {
      provisioned = (await this.deps.provisionLegacy?.(legacy, password)) ?? false;
    } catch {
      provisioned = false;
    }
    if (provisioned) {
      try {
        const result = await this.deps.api.loginUser(email, password);
        return { session: await this.rememberOnlineLogin(result, email, password, tenantId), mode: 'online' };
      } catch {
        /* compte créé mais connexion impossible pour l'instant : ouverture locale ci-dessous */
      }
    }
    const stamp = new Date(this.now()).toISOString();
    await this.deps.users.save({ ...legacy, derniere_connexion: stamp });
    const session: Session = {
      user_id: legacy.id,
      tenant_id: legacy.tenant_id,
      role: legacy.role,
      expire_le: new Date(this.now() + SESSION_DURATION_MS).toISOString(),
      token: uuid('tok'),
    };
    this.deps.persistSession(session);
    return { session, mode: 'legacy' };
  }

  private async rememberOnlineLogin(
    result: { token: string; expires_at: string; user: HybridRemoteUser },
    email: string,
    password: string,
    tenantId: string | undefined,
  ): Promise<Session> {
    const remote = result.user;
    const stamp = new Date(this.now()).toISOString();
    const role = normalizeServerRole(remote.role);
    const salt = this.newSalt();
    const passwordHash = await this.hash(password, salt);
    const existingById = await this.deps.users.findById(remote.id);
    const sameEmail = await this.deps.users.findByEmail(email, remote.tenant_id ?? tenantId);
    const previous = existingById ?? sameEmail;
    const { prenom, nom } = splitName(remote.display_name);
    const user: User = {
      ...(previous ?? {}),
      id: remote.id,
      tenant_id: remote.tenant_id,
      email,
      nom: previous?.nom && !remote.display_name.trim() ? previous.nom : nom,
      prenom: previous?.prenom && !remote.display_name.trim() ? previous.prenom : prenom,
      role,
      password_hash: passwordHash,
      password_salt: salt,
      actif: true,
      identity_verified_at: stamp,
      derniere_connexion: stamp,
      cree_le: previous?.cree_le ?? stamp,
    };
    await this.deps.users.save(user);
    // Ancien compte local du même e-mail (poste Standalone migré) : remplacé par le compte du serveur.
    if (sameEmail && sameEmail.id !== remote.id) await this.deps.users.remove(sameEmail.id);
    this.failures.delete(email);
    const session: Session = {
      user_id: remote.id,
      tenant_id: remote.tenant_id,
      role,
      expire_le: new Date(Math.max(Date.parse(result.expires_at) || 0, this.now() + SESSION_DURATION_MS)).toISOString(),
      token: uuid('tok'),
    };
    this.deps.persistSession(session);
    return session;
  }

  private async loginOffline(email: string, password: string, tenantId: string | undefined): Promise<Session> {
    const gate = this.failures.get(email);
    if (gate?.lockedUntil && gate.lockedUntil > this.now()) throw new AuthError('TOO_MANY_ATTEMPTS');
    const user = await this.deps.users.findByEmail(email, tenantId);
    if (!user || !user.identity_verified_at) {
      // Jamais vu en ligne sur ce poste : impossible de vérifier le compte sans le serveur.
      throw new AuthError('FIRST_LOGIN_ONLINE_REQUIRED');
    }
    if (!user.actif) throw new AuthError('USER_INACTIVE');
    const verifiedAt = Date.parse(user.identity_verified_at);
    if (!Number.isFinite(verifiedAt) || this.now() - verifiedAt > OFFLINE_IDENTITY_MAX_AGE_MS) {
      throw new AuthError('OFFLINE_IDENTITY_EXPIRED');
    }
    const ok = await this.verify(password, user.password_hash, user.password_salt);
    if (!ok) {
      const count = (gate?.count ?? 0) + 1;
      this.failures.set(email, count >= MAX_OFFLINE_ATTEMPTS ? { count, lockedUntil: this.now() + OFFLINE_LOCKOUT_MS } : { count });
      throw new AuthError('INVALID_CREDENTIALS');
    }
    this.failures.delete(email);
    const stamp = new Date(this.now()).toISOString();
    await this.deps.users.save({ ...user, derniere_connexion: stamp });
    const session: Session = {
      user_id: user.id,
      tenant_id: user.tenant_id,
      role: user.role,
      expire_le: new Date(this.now() + SESSION_DURATION_MS).toISOString(),
      token: uuid('tok'),
    };
    this.deps.persistSession(session);
    return session;
  }

  /**
   * Rôle et état renvoyés par le serveur pendant une session en ligne (`GET /v1/sync/status` -> `session`) :
   * le cache d'identité suit le serveur, sans attendre la prochaine connexion.
   */
  async applyServerSession(serverSession: { user_id: string; role: string }): Promise<{ changed: boolean }> {
    const user = await this.deps.users.findById(serverSession.user_id);
    if (!user) return { changed: false };
    const role = normalizeServerRole(serverSession.role);
    const stamp = new Date(this.now()).toISOString();
    const changed = user.role !== role;
    await this.deps.users.save({ ...user, role, identity_verified_at: stamp });
    return { changed };
  }
}
